import { Box, Typography, useTheme } from "@mui/material";
import { useGetCustomerMembers } from "controller/hooks/customer/useCustomerMembers";
import { useMainStore } from "controller/store/mainStore";
import { useAtomValue, useSetAtom } from "jotai";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { ChatMember, CustomerMemberItem } from "shared";
import {
  cachePendingMember,
  memberSearchQuery,
  selectedMemberIds,
  setAvailableCustomerMembersCount,
  setIsCustomerMembersSearching,
  toggleMemberSelection,
} from "../../store/chatMembersStore";
import { MemberListItem } from "./MemberListItem";

interface AvailableMembersListProps {
  open: boolean;
  membersData: ChatMember[] | undefined;
  threadId?: string;
  /** Number of filtered chat members rendered above this list */
  filteredMembersCount: number;
  /** Whether the parent chat members query is still loading */
  isMembersLoading: boolean;
}

/**
 * AvailableMembersList - Shows customer resources + portal users
 * that are not yet in the chat, rendered seamlessly below existing
 * chat members as a single unified list.
 */
export const AvailableMembersList = memo(
  ({
    open,
    membersData,
    threadId,
    filteredMembersCount,
    isMembersLoading,
  }: AvailableMembersListProps) => {
    const theme = useTheme();
    const customerId = useMainStore(
      (state) => state.ticketDetails?.customerId,
    ) as string | undefined;

    const searchQuery = useAtomValue(memberSearchQuery);
    const selectedIds = useAtomValue(selectedMemberIds);
    const toggleSelection = useSetAtom(toggleMemberSelection);
    const addToCache = useSetAtom(cachePendingMember);
    const setAvailableCount = useSetAtom(setAvailableCustomerMembersCount);
    const setSearching = useSetAtom(setIsCustomerMembersSearching);

    // Debounced search for customer members API (300ms)
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
      const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
      return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch customer members (resources + portal users)
    const { data: customerMembersData, isLoading } = useGetCustomerMembers(
      customerId ?? "",
      debouncedSearch || undefined,
      open && Boolean(customerId),
    );

    // Compute available customer members (not already in chat)
    const availableCustomerMembers = useMemo((): CustomerMemberItem[] => {
      if (!customerMembersData) return [];

      const existingMemberEmails = new Set(
        (membersData ?? []).map((m) => m.email?.toLowerCase()).filter(Boolean),
      );

      const resources: CustomerMemberItem[] =
        customerMembersData.resources.result
          .filter((r) => !existingMemberEmails.has(r.email?.toLowerCase()))
          .map((r) => ({
            mapping_id: r.mapping_id,
            email: r.email,
            first_name: r.first_name,
            last_name: r.last_name,
            display_name: r.display_name,
            is_active: r.is_active,
            is_locked: r.is_locked,
            member_type: "resource" as const,
            limited_access: r.limited_access,
          }));

      const users: CustomerMemberItem[] = customerMembersData.users.result
        .filter((u) => !existingMemberEmails.has(u.email?.toLowerCase()))
        .map((u) => ({
          mapping_id: u.mapping_id,
          email: u.email,
          first_name: u.first_name,
          last_name: u.last_name,
          display_name: u.display_name,
          is_active: u.is_active,
          is_locked: u.is_locked,
          member_type: "customer" as const,
        }));

      const combined = [...resources, ...users];

      // Only show inactive / access-restricted users when searching
      if (!searchQuery.trim()) {
        return combined.filter((cm) => cm.is_active && !cm.limited_access);
      }

      return combined;
    }, [customerMembersData, membersData, searchQuery]);

    // Sync available count + searching state so SearchHeader can gate the email popup
    const isDebouncePending = searchQuery !== debouncedSearch;
    useEffect(() => {
      setAvailableCount(availableCustomerMembers.length);
    }, [availableCustomerMembers.length, setAvailableCount]);

    useEffect(() => {
      setSearching(isDebouncePending || isLoading);
    }, [isDebouncePending, isLoading, setSearching]);

    // Toggle handler: cache full member object on select, simple toggle on deselect
    const handleToggle = useCallback(
      (userId: string) => {
        if (selectedIds.has(userId)) {
          toggleSelection(userId);
        } else {
          const cm = availableCustomerMembers.find(
            (m) => m.mapping_id === userId,
          );
          if (cm) {
            addToCache({
              user_id: cm.mapping_id,
              display_name: cm.display_name,
              member_type: cm.member_type,
              member_source: "cc",
              email: cm.email,
            });
          }
        }
      },
      [selectedIds, availableCustomerMembers, addToCache, toggleSelection],
    );

    // Unified "no results" — only when searching and both lists are empty
    const showNoResults =
      searchQuery.length > 0 &&
      !isMembersLoading &&
      !isLoading &&
      filteredMembersCount === 0 &&
      availableCustomerMembers.length === 0;

    if (!customerId) {
      // No customer context — show no-results if chat members list is also empty during search
      if (
        searchQuery.length > 0 &&
        !isMembersLoading &&
        filteredMembersCount === 0
      ) {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: "24px",
            }}
          >
            <Typography
              variant="body-medium"
              sx={{ color: theme.palette.lightBg.low }}
            >
              No results found
            </Typography>
          </Box>
        );
      }
      return null;
    }

    return (
      <>
        {availableCustomerMembers.map((cm) => (
          <MemberListItem
            key={cm.mapping_id}
            member={{
              user_id: cm.mapping_id,
              display_name: cm.display_name,
              member_type: cm.member_type,
              member_source: "cc",
              email: cm.email,
            }}
            isSelected={selectedIds.has(cm.mapping_id)}
            onToggle={handleToggle}
            isLimitedAccess={cm.limited_access === true}
            isInactive={!cm.is_active}
            level={threadId ? "thread" : "ticket"}
          />
        ))}

        {showNoResults && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: "24px",
            }}
          >
            <Typography
              variant="body-medium"
              sx={{ color: theme.palette.lightBg.low }}
            >
              No results found
            </Typography>
          </Box>
        )}
      </>
    );
  },
);

AvailableMembersList.displayName = "AvailableMembersList";
